import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography, alpha,
} from '@mui/material';
import { Add, Remove, CloseRounded, PersonAddAlt1Outlined } from '@mui/icons-material';

import type { CellTeacher, SubjectAssignmentGridCell } from '../../services/sessions/teachingLoadsApi';
import TeacherPickerSelect, { TeacherOption } from './TeacherPickerSelect';

interface Props {
  open: boolean;
  cell: SubjectAssignmentGridCell | null;
  /** The cell's current teachers (pending edits included). */
  initial: CellTeacher[];
  teachers: TeacherOption[];
  onClose: () => void;
  /** One teacher back means the subject is no longer split. */
  onApply: (teachers: CellTeacher[]) => void;
}

interface Row {
  key: number;
  teacher: { _id: string; name: string } | null;
  periods: number;
  doubles: number;
}

/** A − value + control for small counts. */
const Stepper = memo<{ value: number; min: number; max: number; onChange: (v: number) => void; label: string; error?: boolean }>(
  ({ value, min, max, onChange, label, error }) => (
    <Stack direction="row" alignItems="center" spacing={0.25} aria-label={label}
      sx={(theme) => ({
        border: '1px solid', borderColor: error ? 'error.main' : 'divider', borderRadius: '10px', px: 0.25,
        bgcolor: error ? alpha(theme.palette.error.main, 0.04) : 'background.paper',
      })}
    >
      <IconButton size="small" onClick={() => onChange(value - 1)} disabled={value <= min} aria-label={`Fewer ${label}`}><Remove sx={{ fontSize: 16 }} /></IconButton>
      <Typography sx={{ minWidth: 22, textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      <IconButton size="small" onClick={() => onChange(value + 1)} disabled={value >= max} aria-label={`More ${label}`}><Add sx={{ fontSize: 16 }} /></IconButton>
    </Stack>
  ),
);
Stepper.displayName = 'Stepper';

let nextKey = 0;

/**
 * Split a section's subject between teachers: who teaches how many of its
 * weekly periods (and of its double periods). The totals must match the
 * curriculum exactly, the same rule the server applies.
 */
const SplitPeriodsDialog: React.FC<Props> = ({ open, cell, initial, teachers, onClose, onApply }) => {
  const total = cell?.curriculum.periodsPerWeek ?? 0;
  const totalDoubles = cell?.curriculum.doublePeriods ?? 0;
  const [rows, setRows] = useState<Row[]>([]);

  // Start from the current split, or from the one teacher getting everything but one period.
  useEffect(() => {
    if (!open || !cell) return;
    if (initial.length > 1) {
      setRows(initial.map((t) => ({ key: nextKey++, teacher: { _id: t._id, name: t.name }, periods: t.periods ?? 1, doubles: t.doublePeriods ?? 0 })));
    } else {
      const first = initial[0] ?? null;
      setRows([
        { key: nextKey++, teacher: first ? { _id: first._id, name: first.name } : null, periods: Math.max(1, total - 1), doubles: totalDoubles },
        { key: nextKey++, teacher: null, periods: total > 1 ? 1 : 0, doubles: 0 },
      ]);
    }
  }, [open, cell, initial, total, totalDoubles]);

  const sumPeriods = rows.reduce((n, r) => n + r.periods, 0);
  const sumDoubles = rows.reduce((n, r) => n + r.doubles, 0);

  const problem = useMemo(() => {
    if (rows.some((r) => !r.teacher)) return 'Pick a teacher for every row.';
    const ids = rows.map((r) => r.teacher!._id);
    if (new Set(ids).size !== ids.length) return 'A teacher can only appear once.';
    const short = rows.find((r) => r.periods < 1);
    if (short) return `Give ${short.teacher!.name} at least one period.`;
    const tooManyDoubles = rows.find((r) => r.doubles * 2 > r.periods);
    if (tooManyDoubles) return `${tooManyDoubles.teacher!.name} has ${tooManyDoubles.periods} period${tooManyDoubles.periods === 1 ? '' : 's'}, so at most ${Math.floor(tooManyDoubles.periods / 2)} can be double.`;
    if (sumPeriods !== total) return sumPeriods < total ? `${total - sumPeriods} period${total - sumPeriods === 1 ? '' : 's'} still to give out.` : `${sumPeriods - total} period${sumPeriods - total === 1 ? '' : 's'} too many.`;
    if (sumDoubles !== totalDoubles) return `The double period${totalDoubles === 1 ? '' : 's'} must add up to ${totalDoubles}.`;
    return null;
  }, [rows, sumPeriods, sumDoubles, total, totalDoubles]);

  const update = (key: number, patch: Partial<Row>) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const taken = new Set(rows.map((r) => r.teacher?._id).filter(Boolean));

  const apply = () => {
    onApply(rows.map((r) => ({ _id: r.teacher!._id, name: r.teacher!.name, periods: r.periods, doublePeriods: r.doubles })));
  };

  const keepOne = (row: Row) => {
    if (!row.teacher) return;
    onApply([{ _id: row.teacher._id, name: row.teacher.name, periods: null, doublePeriods: null }]);
  };

  const tally = (label: string, n: number, of: number) => {
    const ok = n === of;
    return (
      <Typography
        component="span"
        variant="body2"
        sx={{ fontWeight: 700, color: ok ? 'success.main' : 'warning.dark', fontVariantNumeric: 'tabular-nums' }}
      >
        {n} of {of} {label}
      </Typography>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Split {cell?.subjectName}
        <Typography variant="body2" color="text.secondary">
          {cell?.sectionName ? `Section ${cell.sectionName} · ` : ''}{total} period{total === 1 ? '' : 's'} a week{totalDoubles ? `, ${totalDoubles} of them double` : ''}. Each teacher gets their own periods on the timetable, and either can enter marks.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.25} sx={{ mt: 1.5 }}>
          <Stack direction="row" spacing={2} sx={{ px: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ flex: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>Teacher</Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ width: 104, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.6 }}>Periods</Typography>
            {totalDoubles > 0 && (
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ width: 104, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.6 }}>Doubles</Typography>
            )}
            <Box sx={{ width: 34 }} />
          </Stack>
          {rows.map((r) => (
            <Stack key={r.key} direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TeacherPickerSelect
                  teachers={teachers.filter((t) => t._id === r.teacher?._id || !taken.has(t._id))}
                  value={r.teacher}
                  onChange={(t) => update(r.key, { teacher: t ? { _id: t._id, name: t.name } : null })}
                  placeholder="Pick a teacher"
                />
              </Box>
              <Box sx={{ width: 104, display: 'flex', justifyContent: 'center' }}>
                <Stepper label="periods" value={r.periods} min={1} max={total} onChange={(v) => update(r.key, { periods: v, doubles: Math.min(r.doubles, Math.floor(v / 2)) })} />
              </Box>
              {totalDoubles > 0 && (
                <Box sx={{ width: 104, display: 'flex', justifyContent: 'center' }}>
                  <Stepper label="doubles" value={r.doubles} min={0} max={Math.min(totalDoubles, Math.floor(r.periods / 2))} onChange={(v) => update(r.key, { doubles: v })} />
                </Box>
              )}
              <Tooltip title={rows.length > 2 ? 'Remove' : 'Keep only the other teacher'}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const rest = rows.filter((x) => x.key !== r.key);
                    if (rest.length === 1) keepOne(rest[0]);
                    else setRows(rest);
                  }}
                  aria-label="Remove teacher"
                  sx={{ color: 'text.secondary' }}
                >
                  <CloseRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<PersonAddAlt1Outlined />}
              onClick={() => setRows((prev) => [...prev, { key: nextKey++, teacher: null, periods: 1, doubles: 0 }])}
              disabled={rows.length >= total}
              sx={{ textTransform: 'none' }}
            >
              Add teacher
            </Button>
          </Box>
        </Stack>

        <Box
          sx={(theme) => ({
            mt: 2, px: 1.75, py: 1.25, borderRadius: '10px',
            bgcolor: alpha(problem ? theme.palette.warning.main : theme.palette.success.main, 0.07),
            border: '1px solid', borderColor: alpha(problem ? theme.palette.warning.main : theme.palette.success.main, 0.3),
          })}
        >
          <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
            {tally('periods', sumPeriods, total)}
            {totalDoubles > 0 && <Typography component="span" color="text.disabled">·</Typography>}
            {totalDoubles > 0 && tally(totalDoubles === 1 ? 'double' : 'doubles', sumDoubles, totalDoubles)}
          </Stack>
          <Typography variant="caption" color="text.secondary">{problem ?? 'Ready. Save the page to keep it.'}</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" onClick={apply} disabled={!!problem}>Apply split</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitPeriodsDialog;
